import {
  Box,
  Button,
  CheckFilledIcon,
  DateField,
  ErrorCircleIcon,
  FormContentIsland,
  FormTextField,
  HookForm,
  MenuItem,
  Stack,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { CountryField } from '../../Field/CountryField';
import { ApiMember, ApiError, ApiCreateMember } from '../../../openapi';
import { useForm, useWatch } from 'react-hook-form';
import { UseMutationResult } from '@tanstack/react-query';
import { useFormError } from '../../../hooks/useFormError';
import { useMainLayoutStyles } from '../../Layout/MainLayout';
import { useMemo } from 'react';

type FormValues = ApiCreateMember;

export function MemberForm({
  mutation,
  defaultValues,
}: {
  mutation: UseMutationResult<unknown, ApiError, FormValues>;
  defaultValues?: ApiMember;
}) {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    defaultValues: {
      role: defaultValues?.membership.role ?? 'VIEWER',
      email: defaultValues?.identity.email ?? defaultValues?.identity.unconfirmedEmail ?? undefined,
      legalName: {
        firstName: defaultValues?.individual.name?.firstName ?? undefined,
        lastName: defaultValues?.individual.name?.lastName ?? undefined,
        middleName: defaultValues?.individual.name?.middleName ?? undefined,
      },
      dob: defaultValues?.individual.birthInfo?.birthDate ?? undefined,
      nationality: defaultValues?.individual.nationality ?? undefined,
    },
  });

  useFormError(form, 'email', mutation.error);

  const { spacing } = useMainLayoutStyles();

  const fillingState = useWatch({
    control: form.control,
    compute: (values) => ({
      firstName: Boolean(values.legalName?.firstName),
      lastName: Boolean(values.legalName?.lastName),
      dob: Boolean(values.dob),
      nationality: Boolean(values.nationality),
    }),
  });

  const { isCardsAvailable, personalInfoMessage } = useMemo(() => {
    const unfilledFields = [
      fillingState.firstName ? false : t('First name'),
      fillingState.lastName ? false : t('Last name'),
      fillingState.dob ? false : t('Date of Birth'),
      fillingState.nationality ? false : t('Nationality'),
    ]
      .filter(Boolean)
      .join(', ');
    if (unfilledFields.length > 0) {
      return {
        isCardsAvailable: false,
        personalInfoMessage: t(
          'You have to fill in the following fields to issue a card: {{fields}}',
          {
            fields: unfilledFields,
          },
        ),
      };
    }

    return {
      isCardsAvailable: true,
      personalInfoMessage: t(
        'With all required personal information filled in, you can issue cards to this member.',
      ),
    };
  }, [fillingState, t]);

  return (
    <HookForm
      form={form}
      onSubmit={(values) => mutation.mutate(values)}
      sx={{ width: '100%', maxWidth: 750 }}
    >
      <Stack spacing={spacing} width="100%">
        <FormContentIsland>
          <FormTextField name="role" label={t('Role')} select>
            <MenuItem value="MEMBER">{t('Member')}</MenuItem>
            <MenuItem value="VIEWER">{t('Viewer')}</MenuItem>
            <MenuItem value="PAYER">{t('Payer')}</MenuItem>
            <MenuItem value="ADMIN">{t('Admin')}</MenuItem>
          </FormTextField>
          <FormTextField
            name="email"
            type="email"
            label={t('Email')}
            autoFocus
            autoComplete="off"
            disabled={Boolean(defaultValues)}
          />
          <Box>
            <Typography variant="subtitle2" color="text.tertiary">
              {t('Member personal information')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {isCardsAvailable ? (
                <CheckFilledIcon color="success" />
              ) : (
                <ErrorCircleIcon color="warning" />
              )}
              <Typography variant="body1">{personalInfoMessage}</Typography>
            </Stack>
          </Box>

          <FormTextField
            name="legalName.firstName"
            label={t('First name')}
            required
            autoComplete="off"
          />
          <FormTextField
            name="legalName.lastName"
            label={t('Last name')}
            required
            autoComplete="off"
          />
          <FormTextField
            name="legalName.middleName"
            label={t('Middle name (optional)')}
            autoComplete="off"
          />
          <DateField name="dob" label={t('Date of Birth')} required />
          <CountryField name="nationality" label={t('Nationality')} required />
        </FormContentIsland>
        <Button
          type="submit"
          size="large"
          color="primary"
          loading={mutation.isPending}
          disabled={!form.formState.isDirty}
          fullWidth
        >
          {defaultValues ? t('Update member') : t('Add member')}
        </Button>
      </Stack>
    </HookForm>
  );
}
